<template>
  <div class="termin">
    <h2 class="h4">Wann haben Sie für uns Zeit?</h2>
    <p class="termin__subtitle">
      Die Termine beziehen sich auf den von Ihnen ausgewählten Stützpunkt und sind bereits
      automatisch auf die richtige Dauer angelegt.
    </p>
    <div class="pickerlrechner-cta" v-if="selectedServicesId.includes(SNR_54a)">
      <p class="pickerlrechner-cta__title" v-if="selectedServicesId.includes(SNR_54a)">
        Berechnen Sie hier, wann das Pickerl bei Ihrem Fahrzeug fällig ist:
      </p>
      <a
        class="link"
        href="https://www.oeamtc.at/mitgliedschaft/pruefdienst-leistungen/57a-begutachtung-pickerl/#pickerlrechner-18703749"
        target="_blank"
        >Zum Pickerlrechner</a
      >
    </div>
    <div class="termin__detail">
      <p>Stützpunkt</p>
      <p>
        {{
          activeStation?.bezeichnung +
          ', ' +
          activeStation?.adresse +
          ', ' +
          activeStation?.plz +
          ' ' +
          activeStation?.ort
        }}
      </p>
    </div>
    <div class="termin__detail">
      <p>Termin</p>
      <p v-if="!selectedTimeslot" class="termin__detail__unselected">Bitte aus Kalender wählen</p>
      <p v-else>
        {{
          formatDateTime('day, dateLong', selectedTimeslot[0].datum) +
          ' • ' +
          minutesToHHMM(selectedTimeslot[0].von) +
          ', ca. ' +
          formatDuration(
            (selectedTimeslot[1]?.bis || selectedTimeslot[0]?.bis) - selectedTimeslot[0].von
          )
        }}
      </p>
    </div>
    <template v-if="appointmentsWithSelectedVehicle?.length">
      <hr />
      <h3 class="termin__category">Bestehende Termine mit diesem Fahrzeug</h3>
      <Selection
        v-for="(termin, index) in appointmentsWithSelectedVehicle"
        :key="termin.id"
        :title="formatDateTime('day, dateShort', termin.von) + ' • ' + termin.snrBezeichnung"
        :subtitle="
          termin.dienststelleModel.bezeichnung +
          ' • ' +
          termin.terminFahrzeugModel.marke +
          ' ' +
          termin.terminFahrzeugModel.typ
        "
        :divider="index !== appointmentsWithSelectedVehicle?.length - 1"
      >
        <template v-slot:icon>
          <IconCalendar aria-hidden="true" />
        </template>
        <template v-slot:expansion>
          <div class="appointments__expansion">
            <div class="appointments__expansion__info">
              <p>Angebot</p>
              <p>{{ termin.snrBezeichnung }}</p>
              <p>Fahrzeug</p>
              <p>
                {{
                  termin.terminFahrzeugModel.marke +
                  ' ' +
                  termin.terminFahrzeugModel.typ +
                  ' • ' +
                  termin.terminFahrzeugModel.kennzeichen
                }}
              </p>
              <p>Stützpunkt</p>
              <p>
                {{
                  termin.dienststelleModel.bezeichnung + ' • ' + termin.dienststelleModel.adresse
                }}
              </p>
              <p>Termin</p>
              <p>
                {{
                  formatDateTime('day, dateLong • timeFrom - timeUntil Uhr', termin.von, termin.bis)
                }}
              </p>
              <p>Kosten</p>
              <p>{{ termin.preis }} €</p>
            </div>
          </div>
        </template>
      </Selection>
    </template>
    <hr />
    <div class="termin__actions">
      <template v-if="isMobile">
        <button class="termin__actions__date" @click="toggleCalendar">
          <span class="h4">{{ calendarLabel }}</span>
          <IconChevronDown v-if="!showCalendar" aria-label="Kalender anzeigen" />
          <IconChevronUp v-else aria-label="Kalender schließen" />
        </button>
        <el-dialog
          v-model="showCalendar"
          :show-close="false"
          :lock-scroll="isMobile ? true : false"
          custom-class="date"
        >
          <AppointmentCalendar
            :showCalendar="showCalendar"
            @date="setSingleDateFromCalendar"
            @closeCalendar="closeCalendar"
          />
        </el-dialog>
      </template>
      <template v-else>
        <el-popover trigger="click" placement="bottom-start" width="fit-content">
          <template #reference>
            <button class="termin__actions__date" @click="toggleCalendar">
              <span class="h4">{{ calendarLabel }}</span>
              <IconChevronDown aria-label="Kalender anzeigen" />
            </button>
          </template>
          <AppointmentCalendar :showCalendar="showCalendar" @week="setWeekFromCalendar" />
        </el-popover>
      </template>
      <AppointmentFilterButton :initialDayTime="dayTime" @update:dayTime="handleDayTimeUpdate" />
    </div>
    <TimeslotsMobile
      v-if="isMobile"
      :dayFilters="dayFilters"
      :isDayFiltersSet="isDayFiltersSet"
      :filteredTimeslots="filteredTimeslots"
      :overlappingTimeslots="overlappingTimeslots"
      @monthYearArray="handleMonthYearArray"
    />
    <TimeslotsDesktop
      v-else
      :isDayFiltersSet="isDayFiltersSet"
      :filteredTimeslots="filteredTimeslots"
      :overlappingTimeslots="overlappingTimeslots"
      @monthYearArray="handleMonthYearArray"
    />
    <Navigation
      :isNextButtonDisabled="isNextButtonDisabled"
      @nextStep="$event === 4 && lockTimeslot()"
    />
    <el-dialog v-model="showErrorDialog" custom-class="confirmation" :show-close="false">
      <AppointmentDialog @closeErrorDialog="closeErrorDialog" />
    </el-dialog>
  </div>
</template>

<script>
import { SNR_54a } from '@/utils/constants'
import {
  convertDateFormat,
  convertYYYYMMDDMinutesToDate,
  formatDateTime,
  formatDuration,
  getLastSunday,
  getNextDate,
  getNextDayOfWeek,
  minutesToHHMM,
} from '@/utils/format.js'
import { trackGa4Event } from '@/utils/tracking'
import { mapGetters } from 'vuex'

import IconCalendar from '@/components/icons/IconCalendar.vue'
import IconChevronDown from '@/components/icons/IconChevronDown.vue'
import IconChevronUp from '@/components/icons/IconChevronUp.vue'

import AppointmentCalendar from '@/components/Booking/AppointmentCalendar.vue'
import AppointmentDialog from '@/components/Booking/AppointmentDialog.vue'
import AppointmentFilterButton from '@/components/Booking/AppointmentFilterButton.vue'
import Navigation from '@/components/Booking/Navigation.vue'
import TimeslotsDesktop from '@/components/Booking/TimeslotsDesktop.vue'
import TimeslotsMobile from '@/components/Booking/TimeslotsMobile.vue'
import Selection from '@/components/Selection.vue'

export default {
  components: {
    IconCalendar,
    IconChevronDown,
    IconChevronUp,
    AppointmentCalendar,
    AppointmentDialog,
    AppointmentFilterButton,
    TimeslotsDesktop,
    TimeslotsMobile,
    Navigation,
    Selection,
  },
  data() {
    return {
      calendarLabel: '',
      showCalendar: false,
      showFilter: false,
      reset: false,
      overlappingTimeslots: [],
      showErrorDialog: false,
      SNR_54a,
    }
  },
  activated() {
    // sessionData comes from Home page
    if (this.sessionData && this.kfzartId && this.activeStation) {
      this.reset = false
      this.getNewAppointmentTimeslots()
    }

    // User lands on the fourth step of the TVG 'Termin'
    trackGa4Event({
      ga4Event: 'tvg_step_4',
      ga4ParameterName: 'content_type',
      ga4ParameterValue: this.activeStation.bezeichnung,
    })
  },
  deactivated() {
    // reset everything
    this.reset = true
    this.overlappingTimeslots = []
    this.$store.commit('setSingleDate', convertDateFormat(new Date()))
    this.$store.commit('setFirstDateOfWeek', getLastSunday())
    this.$store.commit('clearTimeslots')
    this.resetDayFilters()
    this.resetDayFiltersStartDates()
  },
  computed: {
    ...mapGetters([
      'isMobile',
      'sessionData',
      'activeStep',
      'dayTime',
      'singleDate',
      'firstDateOfWeek',
      'dayFiltersStartDates',
      'dayFiltersStartDatesIndex',
      'futureAppointments',
      'selectedVehicle',
      'newVehicle',
      'kfzartId',
      'activeStation',
      'activeStationId',
      'selectedServicesId',
      'timeslots',
      'lastTimeslot',
      'selectedTimeslot',
      'lockedTimeslot',
    ]),
    appointmentsWithSelectedVehicle() {
      return this.futureAppointments?.filter(
        (appointment) =>
          appointment.terminFahrzeugModel.fin === this.selectedVehicle?.fin &&
          appointment.terminFahrzeugModel.kennzeichen === this.selectedVehicle?.licensePlate
      )
    },
    dayFilters() {
      return [
        {
          id: 1,
          value: this.dayTime.Montag,
          label: 'Mo',
        },
        {
          id: 2,
          value: this.dayTime.Dienstag,
          label: 'Di',
        },
        {
          id: 3,
          value: this.dayTime.Mittwoch,
          label: 'Mi',
        },
        {
          id: 4,
          value: this.dayTime.Donnerstag,
          label: 'Do',
        },
        {
          id: 5,
          value: this.dayTime.Freitag,
          label: 'Fr',
        },
        {
          id: 6,
          value: this.dayTime.Samstag,
          label: 'Sa',
        },
      ]
    },
    isDayFiltersSet() {
      // eslint-disable-next-line
      const { NurVormittags, NurNachmittags, ...onlyDaysFilter } = this.dayTime
      return !Object.values(onlyDaysFilter).every((day) => !day)
    },
    filteredTimeslots() {
      let filteredTimeslots

      if (this.isMobile) {
        // 4 weeks worth
        const lastDate = getNextDate(this.firstDateOfWeek, 27)
        filteredTimeslots = this.timeslots.filter((timeslot) => timeslot.datum <= lastDate)
      } else {
        if (this.isDayFiltersSet) {
          // only up to 5 dates
          const uniqueDates = [...new Set(this.timeslots.map((timeslot) => timeslot.datum))].slice(
            0,
            5
          )
          filteredTimeslots = this.timeslots.filter((timeslot) =>
            uniqueDates.includes(timeslot.datum)
          )
        } else {
          // only until Saturday
          const saturday = getNextDayOfWeek(this.firstDateOfWeek, 6)
          filteredTimeslots = this.timeslots.filter((timeslot) => timeslot.datum <= saturday)
        }
      }

      const noonInMinutes = 12 * 60

      if (this.dayTime.NurVormittags) {
        filteredTimeslots = filteredTimeslots.filter((timeslot) => timeslot.von <= noonInMinutes)
      } else if (this.dayTime.NurNachmittags) {
        filteredTimeslots = filteredTimeslots.filter((timeslot) => timeslot.von > noonInMinutes)
      }

      return filteredTimeslots
    },
    startDate() {
      return this.firstDateOfWeek === getLastSunday()
        ? convertDateFormat(new Date()) // today
        : this.firstDateOfWeek
    },
    isPreviousDataPopulated() {
      // sessionData etc populated on entering url
      return (
        this.sessionData &&
        this.selectedServicesId.length === this.$route.params.services?.split('+').length &&
        this.kfzartId &&
        this.activeStation
      )
    },
    isNextButtonDisabled() {
      return (
        !this.selectedTimeslot ||
        this.overlappingTimeslots.some(
          (overlappingTimeslot) =>
            overlappingTimeslot.datum === this.selectedTimeslot[0].datum &&
            overlappingTimeslot.von === this.selectedTimeslot[0].von
        )
      )
    },
  },
  watch: {
    activeStep(newValue, oldValue) {
      if (newValue < oldValue) {
        // if user go backwards to change selection
        if (this.selectedTimeslot) this.$store.commit('clearSelectedTimeslot')
      }
    },
    isPreviousDataPopulated() {
      if (this.isPreviousDataPopulated) {
        this.getNewAppointmentTimeslots()
      }
    },
    lastTimeslot() {
      if (this.lastTimeslot) {
        if (this.isDayFiltersSet) {
          // Filtered days only
          const uniqueDates = [...new Set(this.timeslots.map((timeslot) => timeslot.datum))]

          const condition = uniqueDates.length <= (this.isMobile ? 4 : 5)
          if (condition) {
            this.$store.dispatch('getAppointmentTimeslots', {
              date: this.lastTimeslot.datum,
              startTime: this.lastTimeslot.von + 1,
              days: this.dayFilters,
            })
          } else {
            this.$store.commit('setIsTimeslotsLoading', false)
            this.$store.commit('clearLastTimeslot')
          }
        } else {
          // Mobile: 4 weeks worth // Desktop: Monday to Saturday
          const condition =
            this.lastTimeslot.datum < getNextDate(this.firstDateOfWeek, this.isMobile ? 28 : 7)
          if (condition) {
            this.$store.dispatch('getAppointmentTimeslots', {
              date: this.lastTimeslot.datum,
              startTime: this.lastTimeslot.von + 1,
            })
          } else {
            this.$store.commit('setIsTimeslotsLoading', false)
            this.$store.commit('clearLastTimeslot')
          }
        }
      }
    },
    singleDate() {
      this.$store.commit('setFirstDateOfWeek', getLastSunday(this.singleDate))
      this.getNewAppointmentTimeslots()
    },
    dayFiltersStartDatesIndex() {
      // left and right arrows
      this.getNewAppointmentTimeslots()
    },
    selectedTimeslot() {
      if (this.selectedTimeslot) {
        this.getOverlappingFutureAppointments()
      }
    },
  },
  methods: {
    formatDateTime,
    formatDuration,
    minutesToHHMM,
    toggleFilter() {
      this.showFilter = !this.showFilter
    },
    closeFilter() {
      this.showFilter = false
    },
    toggleCalendar() {
      this.showCalendar = !this.showCalendar
    },
    closeCalendar() {
      this.showCalendar = false
    },
    openErrorDialog() {
      this.showErrorDialog = true
    },
    closeErrorDialog() {
      this.showErrorDialog = false
    },
    handleMonthYearArray(array) {
      if (array.length) {
        const monthYears = array.map((monthYear) =>
          new Date(monthYear).toLocaleDateString('de-AT', { month: 'long', year: 'numeric' })
        )
        this.calendarLabel = monthYears.join(' - ')
      } else {
        this.calendarLabel = new Date(
          this.isMobile ? this.singleDate : this.firstDateOfWeek
        ).toLocaleDateString('de-AT', {
          month: 'long',
          year: 'numeric',
        })
      }
    },
    setSingleDateFromCalendar(calendarDate) {
      const day = new Date(calendarDate).getDay()
      if (this.isDayFiltersSet && !this.dayFilters[day - 1]?.value) {
        this.resetDayFilters()
        this.getNewAppointmentTimeslots()
      }

      this.$store.commit('setSingleDate', calendarDate)
    },
    setWeekFromCalendar(calendarWeek) {
      this.$store.commit('setFirstDateOfWeek', calendarWeek)
      if (!this.dayFiltersStartDates.includes(calendarWeek)) {
        this.resetDayFiltersStartDates()
      }
      this.getNewAppointmentTimeslots()
    },
    resetDayFilters() {
      this.$store.commit('setDayTime', {
        Montag: false,
        Dienstag: false,
        Mittwoch: false,
        Donnerstag: false,
        Freitag: false,
        Samstag: false,
        NurVormittags: false,
        NurNachmittags: false,
      })
    },
    saveDayTime(dayTime) {
      this.$store.commit('setDayTime', dayTime)

      if (!this.isMobile) this.resetDayFiltersStartDates()
      this.getNewAppointmentTimeslots()
    },
    removeChip(dayTime) {
      this.$store.commit('setDayTime', {
        ...this.dayTime,
        [dayTime]: false,
      })

      if (!this.isMobile) this.resetDayFiltersStartDates()
      this.getNewAppointmentTimeslots()
    },
    resetDayFiltersStartDates() {
      this.$store.commit('setDayFiltersStartDates', [this.startDate])
      this.$store.commit('setDayFiltersStartDatesIndex', 0)
    },
    getNewAppointmentTimeslots() {
      if (this.reset) return

      this.$store.commit('clearTimeslots')

      // Determine the correct date to use
      let dateToUse
      if (this.isDayFiltersSet) {
        if (this.isMobile) {
          dateToUse = this.singleDate
        } else {
          // Make sure we have a valid date when using day filters
          dateToUse =
            this.dayFiltersStartDates.length &&
            this.dayFiltersStartDatesIndex < this.dayFiltersStartDates.length
              ? this.dayFiltersStartDates[this.dayFiltersStartDatesIndex]
              : this.startDate // Fallback to startDate if dayFiltersStartDates is empty
        }
      } else {
        dateToUse = this.startDate
      }

      this.$store.dispatch(
        'getAppointmentTimeslots',
        this.isDayFiltersSet
          ? {
              date: dateToUse,
              days: this.dayFilters,
            }
          : {
              date: dateToUse,
            }
      )
    },
    async lockTimeslot() {
      try {
        await this.$store.dispatch('lockAppointmentTimeslot')
        this.$store.commit('setActiveStep', 5)
        this.$nextTick(() => {
          this.$store.commit('clearSelectedTimeslot')
        })
        this.recheckStationBookingInfo()
        this.checkMembershipContract()
      } catch (error) {
        // Lock failed - stay on step 4
      }
    },
    recheckStationBookingInfo() {
      this.$store.commit('clearStationBookingInfo')
      this.lockedTimeslot.forEach((timeslot) =>
        this.$store.dispatch('getStationBookingInfo', timeslot.dienstleistungsangebotId)
      )
    },
    checkMembershipContract() {
      this.$store.commit('clearMembershipContractText')
      this.lockedTimeslot.forEach((timeslot) =>
        this.$store.dispatch('checkMembershipContract', timeslot.dienstleistungsangebotId)
      )
    },
    getOverlappingFutureAppointments() {
      const licensePlate = this.selectedVehicle
        ? this.selectedVehicle.licensePlate?.toLowerCase()
        : this.newVehicle.licensePlate?.toLowerCase()
      const selectedDateTime = convertYYYYMMDDMinutesToDate(
        this.selectedTimeslot[0].datum,
        this.selectedTimeslot[0].von
      )

      const overlappingFutureAppointment = this.futureAppointments.filter(
        (appt) =>
          appt.dienststelleModel.dienststelleId === this.activeStationId &&
          appt.terminFahrzeugModel.kennzeichen?.toLowerCase() === licensePlate &&
          new Date(appt.von) <= selectedDateTime &&
          new Date(appt.bis) > selectedDateTime
      )

      if (overlappingFutureAppointment.length) {
        this.openErrorDialog()
        this.overlappingTimeslots = [...this.overlappingTimeslots, this.selectedTimeslot[0]]
      }
    },
    handleDayTimeUpdate(updatedDayTime) {
      this.$store.commit('setDayTime', updatedDayTime)
      this.getNewAppointmentTimeslots()
    },
  },
}
</script>

<style lang="scss">
.termin {
  > h4 {
    margin-bottom: 4px;
  }

  &__subtitle {
    @include subtitle;
  }

  &__detail {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 4px;

    @media (max-width: $breakpoint-max-mobile) {
      flex-direction: column;
      margin-bottom: 16px;
    }

    p:first-child {
      font-weight: $font-weight-medium;
      width: 100px;
    }

    .termin__detail__unselected {
      color: $color-text-secondary;
    }
  }

  &__category {
    @include small-text;
    margin-top: 32px;
    margin-bottom: 4px;
    line-height: $line-height;
    font-weight: $font-weight-regular;
  }

  > hr {
    @include container-divider;
    margin-bottom: 32px;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: space-between;
    margin-bottom: 16px;
    position: relative;

    @media (max-width: $breakpoint-max-mobile) {
      margin-bottom: 24px;
    }

    &__date {
      @include flex-center-align;
      cursor: pointer;
      gap: 4px;
      font-weight: $font-weight-bold;
    }

    &__chips {
      display: flex;
      gap: 8px;
      position: absolute;
      right: 72px;
      max-width: calc(100% - 225px);
      overflow: auto;

      @media (max-width: $breakpoint-max-mobile) {
        margin-right: 0;
        order: 3;
        position: static;
        max-width: 100%;
        width: 100%;
      }
    }

    &__filter {
      @include small-text;
      display: flex;
      background-color: $color-grey-light;
      border-radius: 16px;
      cursor: pointer;
      font-weight: $font-weight-medium;
      gap: 2px;
      padding: 4px 8px;

      @media (min-width: $breakpoint-mobile) {
        position: absolute;
        right: 0;
      }

      &:hover,
      &:focus {
        background-color: $color-grey-dark;
      }
    }
  }

  .navigation {
    margin-top: 40px;
  }

  .pickerlrechner-cta {
    margin-bottom: 24px;
    &__title {
      font-weight: $font-weight-medium;
      margin-bottom: 4px;
    }
  }
}
</style>
